import React from 'react'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { addDays, dayOf, startOfDay } from '../../../../shared/date/local-time'
import { SUPPORT_LABEL } from '../../../../shared/audit/entry-format'

// The words and rules of an organization's API keys (Phase D; plan
// rulings 9 and 17), shared by the organization's page and staff
// oversight. The server is the authority: these only shape what is offered
// and say what it answered.

const DAY = 864e5

// A key that expires sooner than this reads "Expires soon" (core
// credentials.ExpiringWithin).
export const EXPIRING_DAYS = 7

// A key's state: the server's `status` (judged by the database's clock),
// else worked out the same way from its times.
export const keyStatus = (key, now = Date.now()) => {
  if (!key) return 'active'
  if (key.status) return key.status
  if (key.revoked_at) return 'revoked'
  const at = key.expires_at ? Date.parse(key.expires_at) : NaN
  if (Number.isFinite(at) && at <= now) return 'expired'
  if (Number.isFinite(at) && at <= now + EXPIRING_DAYS * DAY) return 'expiring'
  return 'active'
}

// A key that still works (a rotate or revoke means something).
export const isLive = key => ['active', 'expiring'].includes(keyStatus(key))

export const STATUS_WORDS = {
  active: { label: 'Active', tone: 'ok' },
  expiring: { label: 'Expires soon', tone: 'warn', title: `Expires within ${EXPIRING_DAYS} days` },
  expired: { label: 'Expired', tone: 'bad' },
  revoked: { label: 'Revoked', tone: null }
}

export const statusText = status => (STATUS_WORDS[status] || STATUS_WORDS.active).label

export const StatusChip = ({ status }) => {
  const words = STATUS_WORDS[status] || STATUS_WORDS.active
  return <Pill tone={words.tone} title={words.title}>{words.label}</Pill>
}

// What a page says while the organization's partner API access is off and
// it still has live keys (Task 15).
export const ACCESS_OFF_BANNER = 'API access is switched off for this organization; its keys are refused'

// Usage comes from the request log, which drops requests rather than slow
// the API when it is overloaded (ruling 10): it can only under-count.
export const UNDER_COUNT_NOTE = 'Counts may under-count, never over-count: when the request log is overloaded it drops requests rather than slow the API down. Updated every few minutes.'

export const NEVER_EXPIRES_WARNING = 'This key never expires. A key that never expires stays usable until someone revokes it: prefer an expiry, and rotate it before then.'

// A key with no expiry, flagged wherever it shows (spec 2026-10-20 §10): a
// key made before every new key had to expire, or a key bound to an LMS
// connection made with Never.
export const NeverExpiresPill = () => <Pill tone="warn" title={NEVER_EXPIRES_WARNING}>Never</Pill>

// Expiry (spec 2026-10-20 §10; core ParseKeyExpiry, contract §2.3): every
// key a person makes expires, in 30, 90 (the default) or 365 days, or on a
// date at most a year away. "Never" is not offered. A present value is RFC
// 3339 and in the future.
export const EXPIRY_CHOICES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '365 days' },
  { value: 'custom', label: 'Custom date' }
]
export const DEFAULT_EXPIRY = '90'
export const MAX_EXPIRY_YEARS = 1

// The rules a create form follows ({ choices, defaultChoice, maxYears }): an
// organization's key (KEY_EXPIRY), or a key bound to an LMS connection
// (BOUND_EXPIRY), which keeps its own because core's ParseExpiry is
// unchanged for bound keys: 365 days by default, Never offered with its
// warning, at most 3 years.
export const KEY_EXPIRY = { choices: EXPIRY_CHOICES, defaultChoice: DEFAULT_EXPIRY, maxYears: MAX_EXPIRY_YEARS }
export const BOUND_EXPIRY = {
  choices: [...EXPIRY_CHOICES, { value: 'never', label: 'Never' }],
  defaultChoice: '365',
  maxYears: 3
}

const HOUR = 36e5

const yearsLater = (ms, years) => {
  const d = new Date(ms)
  d.setUTCFullYear(d.getUTCFullYear() + years)
  return d.getTime()
}

const yearsText = years => (years === 1 ? 'a year' : `${years} years`)

// The latest instant a key may expire at as this form sends it: the rules'
// maximum less an hour (ruling 20), so a browser clock a little ahead of the
// server's never meets the server's exact limit. A number of days and the end
// of a custom day are both held to it.
const latestExpiry = (now, rules) => yearsLater(now, rules.maxYears) - HOUR

// The days a custom date may be (YYYY-MM-DD, the viewer's zone, as a date
// input's min and max): tomorrow to the last day whose end expiresAtFor
// accepts. The picker then offers only what the rule takes; the server stays
// the authority.
export const customDayRange = (now = Date.now(), rules = KEY_EXPIRY) => {
  const latest = latestExpiry(now, rules)
  let max = dayOf(latest)
  while (startOfDay(addDays(max, 1)) - 1 > latest) max = addDays(max, -1)
  return { min: addDays(dayOf(now), 1), max }
}

// The expires_at a choice sends under `rules`: { value } (an RFC 3339 time,
// or null for never where the rules offer it) or { error } (nothing is
// sent). A custom date runs to the last moment of that day in the viewer's
// zone (23:59:59.999), so the key reads as expiring on the day chosen.
//
// A number of days is that long from now, but never past the rules' maximum
// less an hour (ruling 20): the server's limit is exactly one year, so the
// longest choice for a key a person makes ("365 days") sends one year less an
// hour, and a browser clock a little ahead of the server's does not get a
// 400. A bound key's 365 days is far inside its 3 years and is unchanged. A
// custom day is held to the same margin: its end must come an hour before the
// limit.
export const expiresAtFor = (choice, customDay, now = Date.now(), rules = KEY_EXPIRY) => {
  if (choice === 'never') {
    return rules.choices.some(c => c.value === 'never') ? { value: null } : { error: 'Choose when the key expires.' }
  }
  if (choice === 'custom') {
    if (!customDay) return { error: 'Choose a date for the key to expire.' }
    const end = startOfDay(addDays(customDay, 1)) - 1
    if (!Number.isFinite(end) || end <= now || customDay <= dayOf(now)) return { error: 'Choose a date after today.' }
    if (end > latestExpiry(now, rules)) return { error: `A key can expire at most ${yearsText(rules.maxYears)} from now.` }
    return { value: new Date(end).toISOString() }
  }
  const days = Number(choice)
  const at = Math.min(now + days * DAY, latestExpiry(now, rules))
  return { value: new Date(at).toISOString() }
}

const length = s => [...String(s || '')].length

// A key's name: one line, 1 to 100 characters. null when it's fine.
export const nameProblem = name => {
  const n = length(String(name || '').trim())
  if (n === 0) return 'Give the key a name.'
  if (n > 100) return 'A name can be at most 100 characters.'
  return null
}

export const descriptionProblem = text => (length(String(text || '').trim()) > 500 ? 'A description can be at most 500 characters.' : null)

export const reasonProblem = text => (length(String(text || '').trim()) > 500 ? 'A reason can be at most 500 characters.' : null)

// Rotation's grace (ruling 17): how long the old secret keeps working.
export const GRACE_CHOICES = [
  { hours: 0, label: 'Stop the old secret now' },
  { hours: 1, label: '1 hour' },
  { hours: 24, label: '24 hours' },
  { hours: 168, label: '7 days' }
]
export const DEFAULT_GRACE = 24

export const graceText = hours => {
  if (!hours) return 'The old secret stopped working.'
  const choice = GRACE_CHOICES.find(c => c.hours === hours)
  return `The old secret keeps working for ${choice ? choice.label : `${hours} hours`}.`
}

// A person as the key views name them: the name, else the email.
export const personText = person => (person ? person.name || person.email || 'Someone' : '')

// Who made or revoked a key. A person the view names; else EducationXR
// support, only when the view says so (core's created_by_support /
// revoked_by_support: staff are never named to an organization); else
// someone who has since left the organization (the login is gone).
export const FORMER_MEMBER = 'a former member'
export const byWhom = (person, support) => (person ? personText(person) : support ? SUPPORT_LABEL : FORMER_MEMBER)

// The key's prefix as shown: the first characters, then an ellipsis.
export const prefixText = prefix => (prefix ? `${prefix}…` : '')

// A key bound to one record (Phase D D7-8; core's binding, { kind, id }):
// today only an LMS connection's, made from the connection's drawer. An
// unbound key is the organization's.
export const LMS_CONNECTION_BINDING = 'lms_connection'

const BINDING_WORDS = { [LMS_CONNECTION_BINDING]: 'LMS connection' }

// "Bound to LMS connection 7", or '' for an unbound key.
export const bindingText = key => {
  const binding = key && key.binding
  if (!binding || !binding.kind) return ''
  return `Bound to ${BINDING_WORDS[binding.kind] || binding.kind.replace(/_/g, ' ')} ${binding.id}`
}

// Whether key is bound to exactly that record ({ kind, id }).
export const boundTo = (key, binding) =>
  !!(key && key.binding && binding && key.binding.kind === binding.kind && String(key.binding.id) === String(binding.id))

// The roles a bound key may hold (core a8cb3d1: any other is refused with
// 403 api_key.role_not_for_binding). A key bound to an LMS connection holds
// LMS API read-only or read-write, which core recognizes by what they hold.
// Content's role list ({ id, name, description }) carries no permissions or
// markers, and an organization can't read a role's permissions (content's
// role-org-permission is root's), so the admin knows them by the names
// edxr-cli M5 gives them; the server stays the authority.
export const LMS_KEY_ROLE_NAMES = ['LMS API read-only', 'LMS API read-write']

const ROLES_FOR_BINDING = { [LMS_CONNECTION_BINDING]: role => LMS_KEY_ROLE_NAMES.includes(role.name) }

export const BOUND_ROLE_NOTE = {
  [LMS_CONNECTION_BINDING]: 'A key bound to an LMS connection can only hold LMS API read-only or read-write.'
}

// The roles a key may be given: every role for an unbound key; for a bound
// one only its binding's (none for a kind the admin doesn't know).
export const rolesForKey = (key, roles) => {
  const binding = key && key.binding
  if (!binding || !binding.kind) return roles
  const allowed = ROLES_FOR_BINDING[binding.kind]
  return allowed ? roles.filter(allowed) : []
}
